/** What the processing screen shows for a run: three steps an estimator recognises. */
import type { Run } from "@/api/client";

export type StepState = "waiting" | "active" | "done" | "failed";

export interface ProgressStep {
  key: "classify" | "units" | "takeoff";
  title: string;
  detail: string;
  state: StepState;
}

/** Stages in the order a run passes through them; anything unknown counts as the first. */
const ORDER = [
  "ingest",
  "classify",
  "catalog",
  "link",
  "verify",
  "panels",
  "configurations",
  "results",
];

const rank = (stage: string | null) => {
  const i = ORDER.indexOf(stage ?? "");
  return i < 0 ? 0 : i;
};

type Path = "marks" | "panels" | "configurations" | null;

const PATHS: readonly string[] = ["marks", "panels", "configurations"];

const MARKS_STAGES = ["catalog", "link", "verify"];

/**
 * Which way the run finds units: the mode it chose, else the one it was asked for, else
 * what its stage gives away. `null` while an `auto` run has not yet chosen.
 */
function pathOf(run: Run): Path {
  for (const mode of [run.mode_chosen, run.mode]) {
    if (mode && PATHS.includes(mode)) {
      return mode as Path;
    }
  }
  if (run.stage === "panels" || run.stage === "configurations") {
    return run.stage;
  }
  return MARKS_STAGES.includes(run.stage ?? "") ? "marks" : null;
}

type Shown = "marks" | "panels" | "configurations" | "undecided";

const UNITS_STEP: Record<Shown, { title: string; detail: string }> = {
  configurations: {
    title: "Find configurations",
    detail: "Each configuration view and its panes, read from the drawing",
  },
  panels: {
    title: "Detect panels",
    detail: "Columns from the dimension string, rows from the floor levels",
  },
  marks: {
    title: "Find units",
    detail: "Marks read from the schedule, found on the drawings, then checked",
  },
  undecided: { title: "Find units", detail: "Marks or panels, chosen once the sheets are read" },
};

const TAKEOFF_DETAIL: Record<Shown, string> = {
  configurations: "Units proposed for your review, sheet by sheet",
  panels: "Panels grouped by configuration and counted by size",
  marks: "Units counted by type",
  undecided: "Units counted by type",
};

export function pagesSelected(run: Run): number {
  const selected = run.pages_selected;
  return typeof selected === "number" && selected > 0 ? selected : run.pages_total;
}

/**
 * The checklist for a run. A finished run has every step done; a failed or capped run
 * marks the step it stopped in.
 */
export function stepsFor(run: Run): ProgressStep[] {
  const stopped = run.status === "failed" || run.status === "capped";
  const finished = run.status === "done";
  const at = rank(run.stage);
  const total = pagesSelected(run);
  const classified = Math.min(run.pages_classified, total);

  const state = (from: number, to: number): StepState => {
    if (finished || at > to) {
      return "done";
    }
    if (at >= from) {
      return stopped ? "failed" : run.status === "queued" ? "waiting" : "active";
    }
    return "waiting";
  };

  const classify = state(rank("ingest"), rank("classify"));
  const path = pathOf(run) ?? "undecided";
  return [
    {
      key: "classify",
      title: "Classify sheets",
      detail:
        classify === "waiting"
          ? `${total} sheets waiting`
          : classify === "done" && classified === 0
            ? `${total} sheets read`
            : `${classified} of ${total} sheets read`,
      state: classify,
    },
    {
      key: "units",
      ...UNITS_STEP[path],
      state: state(rank("catalog"), rank("configurations")),
    },
    {
      key: "takeoff",
      title: "Build take-off",
      detail: TAKEOFF_DETAIL[path],
      state: finished ? "done" : at >= rank("results") ? state(rank("results"), 99) : "waiting",
    },
  ];
}

export function isFinished(run: Run): boolean {
  return run.status === "done";
}

export function hasStopped(run: Run): boolean {
  return run.status === "failed" || run.status === "capped";
}

/** "1 min 04 s" for a run in flight, from its start to `now`. */
export function elapsed(fromIso: string | null, now: number): string {
  if (!fromIso) {
    return "";
  }
  const seconds = Math.max(0, Math.round((now - Date.parse(fromIso)) / 1000));
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m} min ${String(s).padStart(2, "0")} s` : `${s} s`;
}

/**
 * Where a finished run opens: the sheet workspace when it read configuration views
 * (ADR-0020), whose units reach the take-off once approved; otherwise the take-off.
 */
export function finishedPath(
  documentId: string,
  run: { id: string; mode_chosen?: string | null },
): string {
  const step = run.mode_chosen === "configurations" ? "review" : "takeoff";
  return `/documents/${documentId}/runs/${run.id}/${step}`;
}
